import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  ["collections", "Collections"],
  ["menu", "Menu"],
  ["how", "How it works"],
  ["contact", "Contact"],
];

export const Nav = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled ? "bg-[#FAF6F0]/80 py-3 shadow-[0_1px_0_#F0DDD3] backdrop-blur-xl" : "py-5"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="nav-logo-button">
          <Logo />
        </button>
        <nav className="hidden items-center gap-9 md:flex">
          {LINKS.map(([id, label]) => (
            <button
              key={id}
              onClick={() => scrollToId(id)}
              data-testid={`nav-link-${id}`}
              className="group relative text-sm font-medium text-cacao"
            >
              {label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-full origin-left scale-x-0 bg-[#E11D48] transition-transform duration-300 group-hover:scale-x-100" />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button
            onClick={() => scrollToId("menu")}
            data-testid="nav-mobile-menu-link"
            className="text-sm font-medium text-cacao md:hidden"
          >
            Menu
          </button>
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => setOpen(true)}
            data-testid="cart-trigger-button"
            className="flex items-center gap-2 rounded-full bg-[#2D1E18] py-2.5 pl-4 pr-2.5 text-sm font-medium text-[#FAF6F0]"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">My Box</span>
            <motion.span
              key={count}
              initial={{ scale: 0.6 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 15 }}
              data-testid="cart-count-badge"
              className="grid h-6 min-w-6 place-items-center rounded-full bg-[#E11D48] px-1.5 text-xs font-semibold"
            >
              {count}
            </motion.span>
          </motion.button>
        </div>
      </div>
    </header>
  );
};
